import React from "react"
import { styled } from "@mui/material/styles"

import { css } from '@emotion/react';
import CategorySelect from "./CategorySelect"
import SampleEventForm from "./SampleEventForm"
import TagSelect from "./TagSelect"
import {
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Popover,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material"
import { MINIMUM_TAG_LIST_LENGTH } from "../utils/samplefocusDefaults"
import { titleize } from "../utils/stringUtils"

import { observer, inject } from "mobx-react"

const EmptyStateContainer = styled('div')`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  padding: 50px;
  position: sticky;
  top: 80px;
`

const MassEditContainer = styled('div')`
  position: sticky;
  top: 80px;
`

const SelectTextToggle = styled('span')`
  color: #039be5;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`

const SFAccordion = styled(Accordion)`
  .MuiAccordionSummary-content.Mui-expanded {
    margin-bottom: 0;
  }
  button {
    font-family: Lato;
  }
`

const AccordionLabel = styled('label')(({ showError }) => ({
  fontWeight: 600,
  cursor: 'pointer',
  color: showError ? '#ed3c3c' : 'inherit',
  '&:hover': {
    color: '#2196f3',
  },
}));

const LoopOneShotSection = styled('div')`
  display: flex;
  align-items: center;
  margin: 0 0 0 20px;

  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`

const SFTextField = styled(TextField)`
  .MuiInputBase-root {
    font-family: Lato;
  }
`

const SFFormControl = styled(FormControl)`
  .MuiInputBase-root,
  .MuiInputLabel-root {
    font-family: Lato;
  }
`

const SFMenuItem = styled(MenuItem)`
  font-family: Lato;
  font-weight: 300;
`

const SFCheckbox = styled(Checkbox)``

const SFChip = styled(Chip)`
  margin: 5px;
  border-radius: 5px;

  .MuiChip-deleteIcon {
    font-size: 10px;
    padding-top: 1px;
    padding-right: 1px;
  }
`

const FilenameText = styled('div')`
  font-size: 12px;
`

const DeleteIcon = styled('i')`
  cursor: pointer;
  font-size: 1em;
  color: #ff0000a8;
`

const CopyTagIcon = styled('i')`
  cursor: pointer;
  color: #039be5;
  margin-left: 10px;
`

@inject("sampleStore", "collectionStore", "userStore")
@observer
class MassEditPane extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      expanded: "description-panel",
      showLoadingSuggestedName: false,
    }
  }

  categorySelectCallback = (categoryId) => {
    const { sampleStore } = this.props
    sampleStore.assignCategoryId(categoryId)
  }

  handleSelectAllClick = () => {
    const { sampleStore, samples } = this.props
    sampleStore.clearStore()
    samples.forEach((sample) => sampleStore.selectSample(sample))
  }

  handleDeselectAllClick = () => {
    const { sampleStore } = this.props
    sampleStore.clearStore()
    // Reset initially open accordion
    this.setState({ expanded: "description-panel" })
  }

  handleAccordionChange = (expandedPanel, isExpanded) => {
    if (isExpanded) {
      this.setState({ expanded: expandedPanel })
    } else {
      this.setState({ expanded: false })
    }
  }

  handleInputChange = (e) => {
    const { sampleStore } = this.props
    sampleStore.assignInputChange(e)
    this.setState({ lastInputChangedAt: Date.now() })
  }

  handleInputTouched = (e) => {
    const { sampleStore } = this.props
    sampleStore.selectedSamples.forEach((sample) => {
      sample.touchField(e.target.name)
    })
  }

  handleLoopOneshotChange = (e) => {
    const isChecked = e.target.checked
    const loopOrOneshotTag = e.target.name
    const { sampleStore } = this.props
    if (isChecked) {
      sampleStore.assignTag(loopOrOneshotTag)
    } else {
      sampleStore.removeTag(loopOrOneshotTag)
    }
    sampleStore.touchField("loop_or_one_shot")
  }

  getInputValue = (fieldName) => {
    const { sampleStore } = this.props
    const sampleFieldValues = sampleStore.selectedSamples.map(
      (sample) => sample[fieldName]
    )
    const uniqSampleFieldValues = _.uniq(sampleFieldValues)
    if (uniqSampleFieldValues.length === 1) {
      return uniqSampleFieldValues[0]
    } else {
      return ""
    }
  }

  fieldHasMultipleValues = (fieldName) => {
    const { sampleStore } = this.props
    const sampleFieldValues = sampleStore.selectedSamples.map(
      (sample) => sample[fieldName]
    )
    const uniqSampleFieldValues = _.uniq(sampleFieldValues)
    return uniqSampleFieldValues.length > 1 ? true : false
  }

  getOriginalFilename = () => {
    const { sampleStore } = this.props
    if (sampleStore.selectedSamples.length === 1) {
      return sampleStore.selectedSamples[0].sample_original_filename
    } else {
      return "Multiple samples selected"
    }
  }

  handleCopyTagsClick = () => {
    const { sampleStore } = this.props
    const selectedTags = sampleStore.tagsInCommon
    const tagsToCopy = selectedTags.join(", ")
    navigator.clipboard.writeText(tagsToCopy)
  }

  handleDeleteIconClick = (e) => {
    e.stopPropagation()
    this.setState({ popoverAnchorEl: e.currentTarget })
  }

  handlePopoverClose = (e) => {
    e.stopPropagation()
    this.setState({ popoverAnchorEl: null })
  }

  handleGetAnotherSuggestionClick = async () => {
    const { sampleStore } = this.props
    this.setState({ showLoadingSuggestedName: true })
    await sampleStore.getSuggestedNames({ setNameToSuggested: false })
    this.setState({ showLoadingSuggestedName: false })
  }

  setSuggestedNameForSelected = () => {
    const { sampleStore } = this.props
    sampleStore.getSuggestedNames({ setNameToSuggested: true })
  }

  handleRemoveFromCollectionClick = async () => {
    const { collectionStore, sampleStore } = this.props
    const collection = collectionStore.focusedCollection

    await Promise.all(
      sampleStore.selectedSamples.map(async (sample) => {
        const didRemove = await collection.removeSample(sample.slug)
        if (didRemove) {
          sampleStore.removeSample(sample)
          sampleStore.deselectSample(sample)
        }
      })
    )
  }

  handleDelete = async (e) => {
    const { sampleStore } = this.props
    e.stopPropagation()
    this.setState({ popoverAnchorEl: null })

    await Promise.all(
      sampleStore.selectedSamples.map(async (sample) => {
        const didDelete = await sample.delete()
        if (didDelete) {
          sampleStore.deselectSample(sample)
          sampleStore.removeSample(sample)
        }
      })
    )

    if (sampleStore.samples.length === 0) {
      const baseURL = window.location.origin
      const redirectURL = baseURL + "/samples/new"
      window.location.href = redirectURL
      return null
    }

    return didDelete
  }

  handlePublishingSwitchChange = (event) => {
    const { sampleStore } = this.props
    const checked = event.target.checked

    sampleStore.selectedSamples.forEach((sample) => {
      sample.setIfPublished(checked)
    })
  }

  renderDeleteIcon() {
    const { popoverAnchorEl } = this.state
    const popoverOpen = Boolean(popoverAnchorEl)
    const popoverId = popoverOpen ? "simple-popover" : undefined

    return (
      // NOTE: Not keyboard accessible
      <>
        <DeleteIcon
          className="far fa-trash-can"
          onClick={this.handleDeleteIconClick}
        ></DeleteIcon>
        <Popover
          id={popoverId}
          open={popoverOpen}
          anchorEl={popoverAnchorEl}
          onClose={this.handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <div
            css={css`
              width: 200px;
              padding: 10px;
            `}
          >
            Are you sure you want to delete the selected samples?
            <div
              css={css`
                padding-top: 10px;
                display: flex;
                justify-content: flex-end;
              `}
            >
              <Button
                size="small"
                color="primary"
                css={css`
                  padding-right: 12px;
                `}
                onClick={this.handlePopoverClose}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={this.handleDelete}
              >
                Yes
              </Button>
            </div>
          </div>
        </Popover>
      </>
    )
  }

  onSuggestedNameChipClick = (name) => {
    const { sampleStore } = this.props
    sampleStore.assignName(name)
  }

  renderSuggestedName() {
    const { sampleStore } = this.props
    const { showLoadingSuggestedName } = this.state
    const suggestedName = sampleStore.selectedSamples[0].suggestedName

    if (sampleStore.selectedSamples.length === 1) {
      const sample = sampleStore.selectedSamples[0]

      if (sample.enabledForSuggestedName) {
        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              height: 34px;
              margin-left: 13px;
            `}
          >
            <span
              css={css`
                color: rgba(0, 0, 0, 0.54);
                font-size: 13px;
                margin-right: 5px;
              `}
            >
              Suggested description:{" "}
            </span>
            {showLoadingSuggestedName ? (
              <span
                css={css`
                  margin: 0 30px;
                `}
              >
                <CircularProgress size={15} />
              </span>
            ) : (
              <>
                <SFChip
                  label={titleize(suggestedName)}
                  onClick={() => this.onSuggestedNameChipClick(suggestedName)}
                  onDelete={() => this.onSuggestedNameChipClick(suggestedName)}
                  size="small"
                  variant="outlined"
                  deleteIcon={<i className="fas fa-plus" aria-hidden="true"></i>}
                  aria-label={`Use suggested description`}
                  role="button"
                />
                <Button
                  variant="text"
                  onClick={this.handleGetAnotherSuggestionClick}
                  aria-label="Get another suggested description"
                >
                  <i className="fas fa-refresh" aria-hidden="true"></i>
                </Button>
              </>
            )}
          </div>
        )
      } else {
        return (
          <Alert severity="info">
            Add some tags or retrieve suggested tags to be able to get a name
            suggestion!
          </Alert>
        )
      }
    } else {
      return (
        <Button variant="outlined" onClick={this.setSuggestedNameForSelected}>
          Generate names for selected samples based on tags
        </Button>
      )
    }
  }

  renderDescriptionAccordion() {
    const { sampleStore, userStore } = this.props
    const musicalKeys = [
      "C",
      "C#",
      "D",
      "D#",
      "E",
      "F",
      "F#",
      "G",
      "G#",
      "A",
      "A#",
      "B",
    ]
    const validationErrors = sampleStore.commonValidationErrors
    const touchedFields = sampleStore.commonTouchedFields
    const canModerate = userStore.currentUser.canModerate

    return (
      <div
        css={css`
          width: 100%;
        `}
      >
        <FilenameText>
          Filename:{"  "}
          {this.getOriginalFilename()}
        </FilenameText>
        <SFTextField
          id="name"
          aria-label="Description input field"
          label={
            this.fieldHasMultipleValues("name")
              ? "Multiple Descriptions"
              : "Description"
          }
          placeholder={
            this.fieldHasMultipleValues("name")
              ? "[Updates all selected]"
              : null
          }
          name="name"
          fullWidth
          margin="normal"
          helperText={
            Boolean(touchedFields.name && validationErrors.name)
              ? "Required. Add a descriptive & accurate name."
              : "Add a descriptive & accurate name."
          }
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            className: 'browser-default',
          }}
          onChange={this.handleInputChange}
          onBlur={this.handleInputTouched}
          error={Boolean(touchedFields.name && validationErrors.name)}
          required
          value={this.getInputValue("name")}
          autoComplete="off"
        />
        {canModerate && this.renderSuggestedName()}
        <div
          css={css`
            display: flex;
            width: 100%;
          `}
        >
          <SFTextField
            id="tempo"
            label="Tempo"
            name="tempo"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            margin="normal"
            variant="outlined"
            inputProps={{ min: 1, max: 999, className: 'browser-default' }}
            fullWidth
            style={{ marginRight: 10 }}
            onChange={this.handleInputChange}
            onBlur={this.handleInputTouched}
            value={this.getInputValue("tempo")}
            error={Boolean(touchedFields.tempo && validationErrors.tempo)}
            helperText={
              Boolean(touchedFields.tempo && validationErrors.tempo)
                ? "If a sample has a tempo, it must be greater than 0. If no tempo leave blank."
                : null
            }
          />

          <SFFormControl
            fullWidth
            variant="outlined"
            margin="normal"
            style={{ marginRight: 10 }}
          >
            <InputLabel id="key">Key</InputLabel>
            <Select
              labelId="key"
              id="select"
              label="key"
              name="key"
              value={this.getInputValue("key")}
              onChange={this.handleInputChange}
              onBlur={this.handleInputTouched}
            >
              <SFMenuItem value="">
                <em>None</em>
              </SFMenuItem>
              {musicalKeys.map((key) => {
                return (
                  <SFMenuItem value={key} key={key}>
                    {key}
                  </SFMenuItem>
                )
              })}
            </Select>
          </SFFormControl>
          <SFFormControl fullWidth variant="outlined" margin="normal">
            <InputLabel id="mode">Mode</InputLabel>
            <Select
              labelId="mode"
              id="select"
              label="mode"
              name="mode"
              value={this.getInputValue("mode")}
              onChange={this.handleInputChange}
              onBlur={this.handleInputTouched}
            >
              <SFMenuItem value="">
                <em>None</em>
              </SFMenuItem>
              <SFMenuItem value="major">Major</SFMenuItem>
              <SFMenuItem value="minor">Minor</SFMenuItem>
            </Select>
          </SFFormControl>
        </div>
      </div>
    )
  }

  renderTagSuggestions() {
    const { sampleStore } = this.props

    const cyaniteTagTypes = [
      "mood",
      "character",
      "genre",
      "movement",
      "instrument",
      "voice",
    ]
    let cyaniteTagCount = 0
    cyaniteTagTypes.forEach((tagType) => {
      const tags = sampleStore.getCyaniteTagsInCommon(tagType)
      cyaniteTagCount += tags.length
    })

    return (
      <>
        {cyaniteTagCount > 0 && (
          <p
            css={css`
              margin: 15px 0 10px 0;
              color: rgba(0, 0, 0, 0.54);
              font-size: 13px;
            `}
          >
            Possible Tag suggestions:
          </p>
        )}
        {cyaniteTagTypes.map((tagType) => {
          const cyaniteTagsInCommon = sampleStore.getCyaniteTagsInCommon(
            tagType
          )

          if (cyaniteTagsInCommon.length > 0) {
            return (
              <div
                css={css`
                  display: flex;
                `}
              >
                <span
                  css={css`
                    margin: 5px 7px 0 5px;
                    color: rgba(0, 0, 0, 0.5);
                    font-size: 12px;
                    min-width: 75px;
                  `}
                >
                  {titleize(tagType)}:
                </span>
                <div>
                  {cyaniteTagsInCommon.map((tag) => {
                    const onChipClick = () => {
                      sampleStore.assignTag(tag)
                      sampleStore.removeCyaniteTag(tag)
                    }
                    return (
                      <SFChip
                        label={titleize(tag)}
                        onClick={onChipClick}
                        onDelete={onChipClick}
                        size="small"
                        variant="outlined"
                        deleteIcon={
                          <i className="fas fa-plus" aria-hidden="true"></i>
                        }
                      />
                    )
                  })}
                </div>
              </div>
            )
          }
        })}
      </>
    )
  }

  renderModeratorTools() {
    const { sampleStore } = this.props
    const { expanded } = this.state
    const samples = sampleStore.selectedSamples
    const sample = samples[0]

    return (
      <SFAccordion
        expanded={expanded === "moderator-panel"}
        onChange={(e, isExpanded) =>
          this.handleAccordionChange("moderator-panel", isExpanded)
        }
      >
        <AccordionSummary
          expandIcon={<i className="fas fa-angle-down" aria-hidden="true"></i>}
        >
          <AccordionLabel>4. Moderator tools.</AccordionLabel>
        </AccordionSummary>
        <AccordionDetails
          css={css`
            padding-top: 0;
          `}
        >
          {expanded === "moderator-panel" && (
            <div
              css={css`
                width: 100%;
              `}
            >
              <FormGroup>
                {sampleStore.areSelectedPublished !== null ? (
                  <FormControlLabel
                    tabIndex={-1}
                    control={
                      <Switch
                        checked={sampleStore.areSelectedPublished}
                        onChange={this.handlePublishingSwitchChange}
                        role="switch"
                        aria-label="Publish/unpublish switch"
                      />
                    }
                    label="Published"
                  />
                ) : (
                  <p>
                    Both published and unpublished samples are selected. Select
                    either only published or unpublished samples to modify their
                    published state.
                  </p>
                )}
                <Divider
                  css={css`
                    margin: 10px 0;
                  `}
                  variant="middle"
                />
                <strong
                  css={css`
                    font-weight: 600;
                  `}
                >
                  History
                </strong>
                {sampleStore.selectedSamples.length === 1 ? (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      flex-wrap: wrap;
                    `}
                  >
                    {sample.events.map((event, idx) => {
                      return (
                        <React.Fragment key={`${event.state}-${idx}`}>
                          {idx > 0 && (
                            <span
                              css={css`
                                margin: 0 8px;
                              `}
                            >
                              →
                            </span>
                          )}
                          <Tooltip
                            title={
                              <div
                                css={css`
                                  font-size: 14px;
                                `}
                              >
                                {event.text_summary}
                              </div>
                            }
                          >
                            <span
                              css={css`
                                margin: 2px 0;
                              `}
                            >
                              {titleize(event.state)}
                            </span>
                          </Tooltip>
                        </React.Fragment>
                      )
                    })}
                  </div>
                ) : (
                  <p>Select only 1 sample to see its history.</p>
                )}
                {sampleStore.areSelectedPublished === false && (
                  <>
                    <Divider
                      css={css`
                        margin: 10px 0;
                      `}
                      variant="middle"
                    />
                    <SampleEventForm sample={sample} />
                  </>
                )}
              </FormGroup>
            </div>
          )}
        </AccordionDetails>
      </SFAccordion>
    )
  }

  renderMassEdit() {
    const { sampleStore, userStore, categories, tagOptions, mode } = this.props
    const { expanded } = this.state

    const uniqSelectedCategoryIds = _.uniq(sampleStore.categoryIds)
    let selectedCategory = ""
    let selectedCategoryId

    if (uniqSelectedCategoryIds.length === 1) {
      selectedCategoryId = uniqSelectedCategoryIds[0]
      selectedCategory = categories[selectedCategoryId]
    } else if (uniqSelectedCategoryIds.length > 1) {
      selectedCategoryId = null
      selectedCategory = { name: "Multiple categories selected" }
    }

    const isSelectedALoop = sampleStore.tagsInCommon.includes("loop")
    const isSelectedAOneshot = sampleStore.tagsInCommon.includes("one shot")

    const validationErrors = sampleStore.commonValidationErrors
    const touchedFields = sampleStore.commonTouchedFields
    let heightOfTagPanel = window.innerHeight - 370
    if (window.innerHeight < 660) {
      heightOfTagPanel = 320
    }
    const showNameError = Boolean(validationErrors.name && touchedFields.name)
    const showCategoryError = Boolean(
      validationErrors.category_id && touchedFields.category_id
    )
    const showTagsError = Boolean(
      validationErrors.tag_list && touchedFields.tag_list
    )
    const showLoopOrOneShotError = Boolean(
      validationErrors.loop_or_one_shot && touchedFields.loop_or_one_shot
    )

    const canModerate = userStore.currentUser.canModerate
    const faAngleDownIcon = "fas fa-angle-down"

    return (
      <MassEditContainer>
        <p
          css={css`
            margin-bottom: 0;
          `}
        >
          <span
            css={css`
              font-size: 14px;
              font-weight: 800;
            `}
          >
            {sampleStore.selectedSamples[0].displayName}{" "}
            {sampleStore.selectedSampleCount > 1
              ? `& ${sampleStore.selectedSampleCount - 1} other `
              : ""}
          </span>
          selected
        </p>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10px;
            margin-top: 10px;
          `}
        >
          <p
            css={css`
              margin: 0;
            `}
          >
            {/* NOTE: Not keyboard accessible */}
            <SelectTextToggle onClick={this.handleSelectAllClick}>
              Select all
            </SelectTextToggle>{" "}
            <span
              css={css`
                padding: 0 5px;
              `}
            >
              |
            </span>{" "}
            {/* NOTE: Not keyboard accessible */}
            <SelectTextToggle onClick={this.handleDeselectAllClick}>
              Deselect all
            </SelectTextToggle>
          </p>
          <div>
            {mode == "edit_collection" && (
              <>
                <SelectTextToggle
                  onClick={this.handleRemoveFromCollectionClick}
                >
                  Remove from collection
                </SelectTextToggle>{" "}
                <span
                  css={css`
                    padding: 0 5px;
                  `}
                >
                  |
                </span>{" "}
              </>
            )}
            {this.renderDeleteIcon()}
          </div>
        </div>
        <SFAccordion
          expanded={expanded === "description-panel"}
          onChange={(e, isExpanded) => {
            this.handleAccordionChange("description-panel", isExpanded)
          }}
        >
          <AccordionSummary
            expandIcon={<i className={faAngleDownIcon} aria-hidden="true"></i>}
          >
            <AccordionLabel showError={showNameError}>
              1.{"  "}Add a description, tempo, and key.{" "}
            </AccordionLabel>
          </AccordionSummary>
          <AccordionDetails>
            {this.renderDescriptionAccordion()}
          </AccordionDetails>
        </SFAccordion>
        <SFAccordion
          expanded={expanded === "category-panel"}
          onChange={(e, isExpanded) => {
            this.handleAccordionChange("category-panel", isExpanded)
          }}
        >
          <AccordionSummary
            expandIcon={<i className={faAngleDownIcon} aria-hidden="true"></i>}
          >
            <AccordionLabel showError={showCategoryError}>
              2.{"  "}Select a category:{" "}
              {selectedCategory && (
                <React.Fragment>
                  {" "}
                  <span
                    css={css`
                      color: #757474;
                      font-size: 14px;
                      margin-left: 5px;
                    `}
                    aria-label={`Category selected: ${_.capitalize(selectedCategory.name)}`}
                  >
                    {/* NOTE: Some issues with TTS */}
                    {_.capitalize(selectedCategory.name)}
                  </span>
                </React.Fragment>
              )}
            </AccordionLabel>
          </AccordionSummary>
          <AccordionDetails>
            <CategorySelect
              categories={categories}
              categorySelectCallback={this.categorySelectCallback}
              selectedCategoryId={selectedCategoryId}
              categorySelectSize={13}
              aria-label="Category select"
            />
          </AccordionDetails>
        </SFAccordion>
        <SFAccordion
          expanded={expanded === "tags-panel"}
          onChange={(e, isExpanded) =>
            this.handleAccordionChange("tags-panel", isExpanded)
          }
        >
          <AccordionSummary
            expandIcon={<i className={faAngleDownIcon} aria-hidden="true"></i>}
          >
            {sampleStore.selectedSampleCount > 1 ? (
              <AccordionLabel>3. Tags in common:</AccordionLabel>
            ) : (
              <AccordionLabel showError={showTagsError}>
                <span
                  css={css`
                    margin-right: 5px;
                  `}
                >
                  3.
                </span>
                {"  "}
                {showTagsError
                  ? `Add at least ${MINIMUM_TAG_LIST_LENGTH} tags`
                  : "Add tags."}
              </AccordionLabel>
            )}
          </AccordionSummary>
          <AccordionDetails
            css={css`
              padding-top: 0;
            `}
          >
            {expanded === "tags-panel" && (
              <div
                css={css`
                  width: 100%;
                `}
              >
                <LoopOneShotSection>
                  <FormControlLabel
                    control={
                      <SFCheckbox
                        checked={isSelectedALoop}
                        onChange={this.handleLoopOneshotChange}
                        name="loop"
                        color="primary"
                        aria-label="Loop checkbox"
                      />
                    }
                    label="Loop"
                  />
                  <FormControlLabel
                    control={
                      <SFCheckbox
                        checked={isSelectedAOneshot}
                        onChange={this.handleLoopOneshotChange}
                        name="one shot"
                        color="primary"
                        aria-label="One shot checkbox"
                      />
                    }
                    label="One shot"
                  />
                </LoopOneShotSection>
                {showLoopOrOneShotError && (
                  <p
                    css={css`
                      color: #ed3c3c;
                      font-size: 13px;
                      margin: -5px 0 10px 20px;
                    `}
                  >
                    A sample must be a loop, a one-shot, or both.
                  </p>
                )}
                <div
                  css={css`
                    display: flex;
                    align-items: baseline;
                  `}
                >
                  <p
                    css={css`
                      margin: 0 0 10px 0;
                      color: rgba(0, 0, 0, 0.54);
                      font-size: 13px;
                    `}
                  >
                    Add enough tags so others can know what to expect before
                    listening.
                  </p>
                  <Tooltip
                    placement="top"
                    title={
                      <div
                        css={css`
                          font-size: 14px;
                        `}
                      >
                        Copy tags to clipboard
                      </div>
                    }
                  >
                    {/* NOTE: Not keyboard accessible */}
                    <CopyTagIcon
                      onClick={this.handleCopyTagsClick}
                      className="far fa-clone"
                      aria-hidden="true"
                    />
                  </Tooltip>
                </div>
                <TagSelect
                  tagOptions={tagOptions}
                  heightOfTagPanel={heightOfTagPanel}
                />
                {this.renderTagSuggestions()}
              </div>
            )}
          </AccordionDetails>
        </SFAccordion>
        {canModerate && mode !== "edit_uploaded" && this.renderModeratorTools()}
      </MassEditContainer>
    )
  }

  render() {
    const { sampleStore } = this.props

    if (
      sampleStore.selectedSampleCount === 0 ||
      sampleStore.sampleCount === 0
    ) {
      return (
        <EmptyStateContainer>
          <p
            css={css`
              font-size: 30px;
              font-weight: 800;
            `}
          >
            Select a sample to add details
          </p>
          <p
            css={css`
              font-size: 18px;
              text-align: center;
              margin-bottom: 40px;
            `}
          >
            Tip: To select multiple samples hold "Command/Control" or "Shift".
          </p>
          <Divider
            variant="middle"
            css={css`
              width: 100%;
            `}
          />
          <p
            css={css`
              font-size: 16px;
              text-align: center;
              margin-bottom: 0;
              position: relative;
              top: -30px;
              padding: 0 15px;
              background-color: whitesmoke;
            `}
          >
            Or
          </p>
          <SelectTextToggle
            css={css`
              font-size: 18px;
              text-align: center;
            `}
            onClick={this.handleSelectAllClick}
          >
            Select all
          </SelectTextToggle>
        </EmptyStateContainer>
      )
    } else {
      return this.renderMassEdit()
    }
  }
}

export default MassEditPane
